/* ================================================================
   DROP DEAD — Course cross-sell add-to-cart
   ----------------------------------------------------------------
   Single-variant cross-sell cards add directly to the cart via the
   same rate-limit-safe queue product-form.js uses
   (ddCartHelpers.enqueueExternalMutation), then open the cart drawer.

   Why reuse enqueueExternalMutation instead of a bare fetch:
     • It serializes cart mutations so this ATC can't collide with an
       in-flight cart-drawer update (prevents the "added twice /
       not reflected" race the cart queue was built to solve).
     • invalidateFreshCart: true forces the next drawer paint to pull
       fresh section HTML, so the new line shows up immediately.
     • Reuses the 429 retry already baked into the queue.

   Rate budget per ATC (see rate-limit-awareness rule):
     1 request — POST /cart/add.js with bundled sections. That's it.
     The drawer re-paint uses the sections HTML returned in the same
     response (no second GET /cart.js).
   ================================================================ */
(function () {
  'use strict';

  if (window.DDCourseXsell) return;
  window.DDCourseXsell = true;

  /* ================================================================
     Regios DOPP pricing — generic (cross-product) form
     ----------------------------------------------------------------
     Cross-sell cards render products that are NOT the current PDP
     product, so we MUST use the generic
       api.v0.calculateDiscountedPrices({ productId, variantId, regularPriceInCents })
     and NOT api.v0.productPage.calculateDiscountedPrices (which is
     scoped to the current PDP product and returns nothing useful for
     other products — see dd-card2.js lines 43-49 for the same finding).

     IMPORTANT: dd-cart-cross-sell.js is globally loaded and DOES match
     our [data-dd-upsell-card][data-variant-price] attrs, but its
     applyRegiosToCard() prefers v0.productPage.* when present — which
     on the course PDP would call the wrong (PDP-scoped) API and yield
     no discount. We therefore drive our own Regios lookup here with the
     correct generic API, and de-duplicate against the cart script by
     removing the attributes it keys off if that path ever double-fires.
     Pattern (event + poll fallback + per-variant cache) mirrors
     dd-card2.js so behavior is consistent across card surfaces.
     ================================================================ */
  var regiosV0 = null;
  var regiosListening = false;
  var regiosCache = Object.create(null); // { [variantId]: entry | null }
  var regiosInFlight = Object.create(null);

  function formatMoney(cents) {
    var fmt = window.shopMoneyFormat
      ? String(window.shopMoneyFormat).replace(/<[^>]*>/g, '')
      : '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    return out.replace(/([.,])00(?!\d)/, '');
  }

  function onRegiosReady(v0) {
    regiosV0 = v0;
    processAllCardPricing();
  }

  function startRegiosWatcher() {
    if (regiosListening) return;
    regiosListening = true;

    window.addEventListener('regios-dopp:api-initialized', function (e) {
      var v0 =
        (e && e.detail && e.detail.api) ||
        (window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0);
      if (v0 && !regiosV0) onRegiosReady(v0);
    });

    if (window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0) {
      onRegiosReady(window.RegiosDOPP.api.v0);
      return;
    }
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      var v0 = window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0;
      if (v0) {
        clearInterval(timer);
        if (!regiosV0) onRegiosReady(v0);
      } else if (attempts >= 60) {
        clearInterval(timer);
      }
    }, 500);
  }

  function fetchRegios(productId, variantId, priceCents) {
    var vid = String(variantId);
    if (regiosCache[vid] !== undefined) return Promise.resolve(regiosCache[vid]);
    if (regiosInFlight[vid]) return regiosInFlight[vid];
    if (!regiosV0 || typeof regiosV0.calculateDiscountedPrices !== 'function') {
      return Promise.resolve(null);
    }

    var p = regiosV0
      .calculateDiscountedPrices({
        productId: parseInt(productId, 10),
        variantId: parseInt(vid, 10),
        regularPriceInCents: parseInt(priceCents, 10)
      })
      .then(function (result) {
        if (!result || !result.salePrice || !result.regularPrice) {
          regiosCache[vid] = null;
          return null;
        }
        var saleAmt = parseFloat(result.salePrice.amount);
        var regAmt = parseFloat(result.regularPrice.amount);
        if (!(saleAmt < regAmt)) {
          regiosCache[vid] = null;
          return null;
        }
        var entry = {
          saleCents: Math.round(saleAmt * 100),
          compareCents: Math.round(regAmt * 100)
        };
        regiosCache[vid] = entry;
        return entry;
      })
      .catch(function () { return null; })
      .finally(function () { delete regiosInFlight[vid]; });

    regiosInFlight[vid] = p;
    return p;
  }

  function applyPricingToCard(card) {
    var productId = card.getAttribute('data-product-id');
    var variantId = card.getAttribute('data-variant-id');
    var priceCents = parseInt(card.getAttribute('data-variant-price') || '0', 10);
    if (!productId || !variantId || !priceCents) return;

    var priceEl = card.querySelector('[data-dd-upsell-price]');
    if (!priceEl) return;

    fetchRegios(productId, variantId, priceCents).then(function (entry) {
      if (!entry) return; // no discount — Liquid baseline price stays
      priceEl.textContent = formatMoney(entry.saleCents);
      priceEl.classList.add('dd-upsell__price--sale');

      var compareEl = card.querySelector('[data-dd-upsell-compare]');
      if (compareEl) {
        compareEl.textContent = formatMoney(entry.compareCents);
        compareEl.style.display = '';
      }
    });
  }

  function processAllCardPricing() {
    var cards = document.querySelectorAll('[data-dd-course-xsell-card]');
    for (var i = 0; i < cards.length; i++) {
      applyPricingToCard(cards[i]);
    }
  }

  function setLoading(btn) {
    btn.classList.add('is--loading');
    btn.classList.remove('is--done');
    btn.disabled = true;
  }

  function setDone(btn) {
    btn.classList.remove('is--loading');
    btn.classList.add('is--done');
    btn.disabled = false;
    setTimeout(function () {
      btn.classList.remove('is--done');
    }, 1800);
  }

  function setIdle(btn) {
    btn.classList.remove('is--loading', 'is--done');
    btn.disabled = false;
  }

  function getSectionsToRender() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        return drawer.getSectionsToRender().map(function (s) { return s.id; });
      } catch (e) { /* fall through */ }
    }
    return [];
  }

  function openCartDrawer(triggerEl) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (typeof drawer.open === 'function') {
      drawer.open(triggerEl);
    } else {
      drawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    }
  }

  function buildConfig(variantId) {
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    var sectionIds = getSectionsToRender();
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }
    formData.append('sections_url', window.location.pathname);
    return { body: formData };
  }

  function runAdd(variantId) {
    var config = buildConfig(variantId);
    var attempt = 0;

    var doFetch = function () {
      return fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: config.body
      }).then(function (response) {
        if (response.status === 429 && attempt < 2) {
          attempt++;
          return new Promise(function (resolve) {
            setTimeout(resolve, 650 * attempt);
          }).then(doFetch);
        }
        return response.json();
      });
    };

    return doFetch();
  }

  function paintDrawerFromResponse(parsedState) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !parsedState || !parsedState.sections) return;

    var htmlCache = {};
    Object.keys(parsedState.sections).forEach(function (sectionId) {
      htmlCache[sectionId] = new DOMParser().parseFromString(
        parsedState.sections[sectionId],
        'text/html'
      );
    });

    if (typeof drawer.getSectionsToRender === 'function') {
      drawer.getSectionsToRender().forEach(function (section) {
        var sourceDoc = htmlCache[section.section];
        if (!sourceDoc) return;
        var sourceEl = section.selector
          ? sourceDoc.querySelector(section.selector)
          : sourceDoc.getElementById(section.id);
        var destEl = document.getElementById(section.id);
        if (sourceEl && destEl) {
          destEl.innerHTML = sourceEl.innerHTML;
        }
      });
    }

    drawer.classList.remove('is-empty');
    if (parsedState.item_count === 0) {
      drawer.classList.add('is-empty');
    }
  }

  function handleAdd(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    if (btn.classList.contains('is--loading') || btn.classList.contains('is--done')) return;

    setLoading(btn);

    var helpers = window.ddCartHelpers;
    var addPromise;

    if (helpers && typeof helpers.enqueueExternalMutation === 'function') {
      addPromise = helpers.enqueueExternalMutation({
        label: 'course-xsell-add',
        requester: 'dd-course-xsell',
        endpoint: window.routes.cart_add_url,
        action: 'course-xsell-add',
        run: function () { return runAdd(variantId); },
        invalidateFreshCart: true
      });
    } else {
      addPromise = runAdd(variantId);
    }

    addPromise
      .then(function (parsedState) {
        if (parsedState && parsedState.status) {
          var msg = parsedState.description || parsedState.message || 'Could not add to cart.';
          setIdle(btn);
          if (window.ddToast) {
            window.ddToast(msg, { type: 'error' });
          }
          return;
        }

        if (
          parsedState &&
          Array.isArray(parsedState.items) &&
          typeof parsedState.item_count === 'number' &&
          helpers &&
          typeof helpers.seedLatestCart === 'function'
        ) {
          helpers.seedLatestCart(parsedState);
        }

        paintDrawerFromResponse(parsedState);

        if (
          typeof window.PUB_SUB_EVENTS !== 'undefined' &&
          typeof window.subscribe === 'function'
        ) {
          window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {});
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'dd-course-xsell',
            cartData: parsedState,
            variantId: variantId
          });
        }

        setDone(btn);
        openCartDrawer(btn);
      })
      .catch(function () {
        setIdle(btn);
        if (window.ddToast) {
          window.ddToast('Something went wrong. Please try again.', { type: 'error' });
        }
      });
  }

  function bind() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-dd-course-xsell-atc]');
      if (!btn) return;
      e.preventDefault();
      handleAdd(btn);
    });
    // Kick off Regios DOPP pricing lookup for every card once the API is ready.
    startRegiosWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
